import { memo, MouseEvent, useMemo, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import {
  Box,
  Divider,
  InputAdornment,
  ListSubheader,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material'
import {
  CategoryOutlined,
  ChevronRight,
  ErrorOutline,
  ExpandMore,
  Inventory2Outlined,
  SaveOutlined,
  SearchOutlined,
  UndoOutlined,
  UnfoldLessOutlined,
  UnfoldMoreOutlined,
} from '@mui/icons-material'
import { Button } from 'Common/Supplyvue-ui'
import { KpiStrip, KpiTile } from 'Common/Components/KpiTile'
import { SegmentedControl } from 'Common/Components/SegmentedControl'
import { NavigationModal } from 'Features/Projects/Table/NavigationModal'
import { useSettingsContext } from 'Features/Projects/Scenarios/EditSettings/EditSettings.types'
import { LinePreviewNote } from '../LinePreviewNote'
import { RowProblem } from '../lineRecalculation'
import { CYCLES, QUICK_FILTERS, SET_BY } from '../cycleColumns'
import { TotalRow } from './totalRows'
import { OverrideEdit, useCycleEdits } from './useCycleEdits'

/**
 * Total production, drawn like the Scheduler's By kitchen view: each batch
 * group is a header row that collapses, its SKU groups under it, and on the
 * right a track showing each row's cycle range on the 1 / 2 / 4 / 8-week
 * scale.
 *
 * On the track:
 * - the bar runs from the min cycle to the max cycle (amber when an
 *   override is set, a pin at the overridden end);
 * - the tick is the natural cycle (weeks one minimum order lasts);
 * - hatching on a SKU group is faster than its batch's min cycle, which it
 *   can't run at.
 *
 * Click a step to set the min or max override there, fix the cycle, or
 * clear an override. Edits are previewed through the API's cycle rules
 * (useCycleEdits), so a batch change moves its SKU groups' bars at once.
 */

const RAIL = 300
const NUM_W = 84
const OVR_W = 64
const CYCLE_W = 72
const TYPE_W = 56
const TRACK_MIN = 320
const ROW_H = 36
const MIN_WIDTH = RAIL + 3 * NUM_W + TRACK_MIN + 2 * OVR_W + CYCLE_W + TYPE_W

// The left edge of a row: what set its cycle, at a glance.
const BAND: Record<string, string> = {
  MOQ: 'var(--state-complete)',
  MBQ: 'var(--state-complete)',
  Batch: 'var(--brand-blue)',
  Override: 'var(--amber-fg)',
  FOQ: 'var(--state-running)',
  'No demand': 'var(--ink-300)',
}

const isBatch = (row: TotalRow) => row.rowKind === 'batches'
// The batch a row sits under: the first step of its tree path.
const batchOf = (row: TotalRow) => row.path[0] ?? ''
const num = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

/** Centre of a cycle step on the track, in %. */
const stepCentre = (cycle: number) =>
  ((CYCLES.indexOf(cycle) + 0.5) / CYCLES.length) * 100

/** Where the natural cycle falls on the (log) track, in %. */
const naturalAt = (weeks: number) =>
  Math.min(
    98,
    Math.max(2, ((0.5 + Math.log2(Math.max(weeks, 0.01))) / 4) * 100)
  )

const fmt = (value: unknown, digits = 0) => {
  const n = num(value)
  return n === null
    ? '—'
    : n.toLocaleString(undefined, { maximumFractionDigits: digits })
}

const cellSx = {
  width: NUM_W,
  minWidth: NUM_W,
  px: '8px',
  textAlign: 'right' as const,
  fontFamily: 'var(--font-mono)',
  fontVariantNumeric: 'tabular-nums',
  fontSize: 11.5,
  color: 'var(--ink-700)',
}

type Group = { header: TotalRow; children: TotalRow[]; synthetic?: boolean }

// ─── The track ───────────────────────────────────────────────────────────────

const CycleTrack = ({
  row,
  edited,
  previewed,
  problem,
  onStep,
}: {
  row: TotalRow
  edited: boolean
  previewed: boolean
  problem: boolean
  onStep: (cycle: number, anchor: HTMLElement) => void
}) => {
  const min = num(row.minCycle) ?? 1
  const max = num(row.maxCycle) ?? 8
  const low = Math.min(min, max)
  const natural = num(row.naturalCycle)
  const batchMin = isBatch(row) ? null : num(row.batchMin)
  const overridden = row.minOverride != null || row.maxOverride != null
  const left = stepCentre(CYCLES.includes(low) ? low : 1)
  const right = stepCentre(CYCLES.includes(max) ? max : 8)

  const fill = problem
    ? 'var(--state-failed-tint)'
    : overridden
      ? 'var(--amber-tint)'
      : 'var(--brand-blue-50)'
  const edge = problem
    ? 'var(--state-failed)'
    : overridden
      ? 'var(--amber-fg)'
      : 'var(--brand-blue)'

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: TRACK_MIN,
        position: 'relative',
        alignSelf: 'stretch',
        borderLeft: '1px solid var(--line-200)',
        borderRight: '1px solid var(--line-200)',
        opacity: row.cycleSetBy === 'No demand' ? 0.45 : 1,
      }}
    >
      {CYCLES.map((cycle, i) => {
        const blocked = batchMin !== null && cycle < batchMin
        return (
          <Box
            key={cycle}
            component="button"
            type="button"
            title={
              blocked
                ? `${cycle} wk is faster than its batch's min cycle (${batchMin} wk)`
                : `${cycle} wk: click to set an override here`
            }
            onClick={(e: MouseEvent<HTMLElement>) =>
              onStep(cycle, e.currentTarget)
            }
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${i * 25}%`,
              width: '25%',
              border: 'none',
              borderLeft: i ? '1px solid var(--line-100)' : 'none',
              p: 0,
              cursor: 'pointer',
              background: blocked
                ? 'repeating-linear-gradient(135deg, var(--line-200) 0 2px, transparent 2px 6px)'
                : 'transparent',
              '&:hover': { background: 'var(--teal-tint)' },
              '&:focus-visible': {
                outline: '2px solid var(--focus-ring)',
                outlineOffset: -2,
              },
            }}
          />
        )
      })}
      {/* The allowed range, min to max */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: `calc(${left}% - 9px)`,
          width: `calc(${right - left}% + 18px)`,
          height: 14,
          transform: 'translateY(-50%)',
          borderRadius: '7px',
          background: fill,
          border: `1px ${edited ? 'dashed' : 'solid'} ${edge}`,
          boxShadow: previewed ? '0 0 0 2px var(--brand-blue-50)' : 'none',
          pointerEvents: 'none',
        }}
      >
        {row.minOverride != null && (
          <Box
            sx={{
              position: 'absolute',
              left: 3,
              top: 3,
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: edge,
            }}
          />
        )}
        {row.maxOverride != null && (
          <Box
            sx={{
              position: 'absolute',
              right: 3,
              top: 3,
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: edge,
            }}
          />
        )}
      </Box>
      {/* The natural cycle */}
      {natural !== null && (
        <Box
          sx={{
            position: 'absolute',
            left: `${naturalAt(natural)}%`,
            top: 6,
            bottom: 6,
            width: '2px',
            ml: '-1px',
            background: 'var(--ink-700)',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  )
}

// ─── A row ───────────────────────────────────────────────────────────────────

const CycleRow = memo(
  ({
    row,
    header,
    childCount,
    collapsed,
    onToggle,
    edited,
    previewed,
    problems,
    onStep,
  }: {
    row: TotalRow
    header: boolean
    childCount?: number
    collapsed?: boolean
    onToggle?: () => void
    edited: boolean
    previewed: Set<string> | undefined
    problems: RowProblem[]
    onStep: (row: TotalRow, cycle: number, anchor: HTMLElement) => void
  }) => {
    const blocking = problems.some((p) => p.blocking)
    const setBy = String(row.cycleSetBy ?? '')
    const bg = header ? 'var(--panel-head)' : 'var(--surface-0)'
    const changedCycle =
      previewed?.has('minCycle') || previewed?.has('maxCycle')
    const min = num(row.minCycle)
    const max = num(row.maxCycle)
    const title = header
      ? row.label
      : String(row.skuGroupName || row.label || '')
    const detail = header
      ? `${row.workCentreCode ?? ''} · ${row.baseSourceSiteCode ?? ''} · ${childCount ?? 0} SKU group${childCount === 1 ? '' : 's'}`
      : null

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: ROW_H,
          minWidth: MIN_WIDTH,
          borderBottom: '1px solid var(--line-100)',
          background: edited ? 'var(--surface-warn)' : bg,
        }}
      >
        {/* Sticky rail */}
        <Box
          sx={{
            width: RAIL,
            minWidth: RAIL,
            alignSelf: 'stretch',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            position: 'sticky',
            left: 0,
            zIndex: 2,
            pl: header ? '8px' : '26px',
            pr: '10px',
            borderLeft: header
              ? 'none'
              : `2px solid ${BAND[setBy] ?? 'var(--line-200)'}`,
            background: edited ? 'var(--surface-warn)' : bg,
          }}
        >
          {header && (
            <Box
              component="button"
              type="button"
              aria-label={collapsed ? 'Expand' : 'Collapse'}
              onClick={onToggle}
              sx={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                p: 0,
                display: 'flex',
                color: 'var(--ink-500)',
              }}
            >
              {collapsed ? (
                <ChevronRight sx={{ fontSize: 16 }} />
              ) : (
                <ExpandMore sx={{ fontSize: 16 }} />
              )}
            </Box>
          )}
          {header ? (
            <Inventory2Outlined
              sx={{ fontSize: 13, color: 'var(--state-complete)' }}
            />
          ) : (
            <CategoryOutlined sx={{ fontSize: 13, color: 'var(--ink-400)' }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              title={title}
              sx={{
                fontSize: header ? 11.5 : 11,
                fontWeight: header ? 700 : 600,
                color: 'var(--ink-900)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Box>
            <Box
              sx={{
                fontSize: 9.5,
                color: 'var(--ink-500)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.2,
              }}
            >
              {detail ?? (
                <>
                  <Box component="span" sx={{ fontFamily: 'var(--font-mono)' }}>
                    {row.label}
                  </Box>
                  {setBy && (
                    <Tooltip title={SET_BY[setBy] ?? ''}>
                      <span>{` · ${setBy}`}</span>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>
          </Box>
          {problems.length > 0 && (
            <Tooltip title={problems.map((p) => p.message).join(' · ')}>
              <ErrorOutline
                sx={{
                  fontSize: 15,
                  color: blocking ? 'var(--state-failed)' : 'var(--amber-fg)',
                }}
              />
            </Tooltip>
          )}
        </Box>

        <Box sx={cellSx}>{fmt(row.salesRate, 1)}</Box>
        <Box sx={cellSx}>{fmt(row.minOrder)}</Box>
        <Box sx={cellSx}>{fmt(row.naturalCycle, 1)}</Box>

        {row.id.startsWith('orphan|') ? (
          // SKU groups with no batch row: nothing to draw or edit here.
          <Box
            sx={{
              flex: 1,
              minWidth: TRACK_MIN,
              alignSelf: 'stretch',
              borderLeft: '1px solid var(--line-200)',
              borderRight: '1px solid var(--line-200)',
            }}
          />
        ) : (
          <CycleTrack
            row={row}
            edited={edited}
            previewed={Boolean(changedCycle)}
            problem={blocking}
            onStep={(cycle, anchor) => onStep(row, cycle, anchor)}
          />
        )}

        {(['minOverride', 'maxOverride'] as const).map((field) => (
          <Box
            key={field}
            sx={{
              ...cellSx,
              width: OVR_W,
              minWidth: OVR_W,
              textAlign: 'center',
              color: row[field] != null ? 'var(--amber-fg)' : 'var(--ink-300)',
              fontWeight: row[field] != null ? 600 : 400,
            }}
          >
            {row[field] != null ? String(row[field]) : '—'}
          </Box>
        ))}
        <Box
          sx={{
            ...cellSx,
            width: CYCLE_W,
            minWidth: CYCLE_W,
            textAlign: 'center',
            fontWeight: 600,
            color: changedCycle ? 'var(--brand-blue-600)' : 'var(--ink-900)',
          }}
        >
          {min === null ? '—' : min === max ? `${min}` : `${min}–${max}`}
        </Box>
        <Box
          sx={{
            ...cellSx,
            width: TYPE_W,
            minWidth: TYPE_W,
            textAlign: 'center',
            fontFamily: 'inherit',
            fontWeight: 600,
            fontSize: 10.5,
            color:
              row.replenishmentTypeCode === 'FOQ'
                ? 'var(--state-running)'
                : 'var(--ink-500)',
          }}
        >
          {String(row.replenishmentTypeCode ?? '')}
        </Box>
      </Box>
    )
  }
)
CycleRow.displayName = 'CycleRow'

const HeaderCell = ({
  width,
  children,
  align = 'right',
}: {
  width: number
  children: string
  align?: 'left' | 'right' | 'center'
}) => (
  <Box sx={{ width, minWidth: width, px: '8px', textAlign: align }}>
    {children}
  </Box>
)

// ─── The view ────────────────────────────────────────────────────────────────

export const TotalProductionCycles = ({
  rows,
  allRows,
}: {
  /** The rows on the selected site / line. */
  rows: TotalRow[]
  /** Every row, so the edits survive a filter change. */
  allRows: TotalRow[]
}) => {
  const { state } = useSettingsContext()
  const { projectId, scenarioId } = state
  const editor = useCycleEdits({ projectId, scenarioId, rows: allRows })
  const [quick, setQuick] = useState('all')
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [menu, setMenu] = useState<{
    anchor: HTMLElement
    row: TotalRow
    cycle: number
  } | null>(null)

  const blocker = useBlocker(editor.pendingCount > 0)

  const viewed = useMemo(() => rows.map(editor.view), [rows, editor.view])

  const groups = useMemo((): Group[] => {
    const children = new Map<string, TotalRow[]>()
    viewed
      .filter((r) => !isBatch(r))
      .forEach((r) =>
        children.set(batchOf(r), [...(children.get(batchOf(r)) ?? []), r])
      )
    const out: Group[] = viewed.filter(isBatch).map((header) => ({
      header,
      children: children.get(batchOf(header)) ?? [],
    }))
    const known = new Set(out.map((g) => batchOf(g.header)))
    // SKU groups whose batch has no batch row: a header that can't be edited.
    children.forEach((kids, path) => {
      if (known.has(path)) return
      const first = kids[0] as TotalRow
      out.push({
        synthetic: true,
        children: kids,
        header: {
          ...first,
          id: `orphan|${path}`,
          rowKind: 'batches',
          path: [path],
          label: String(first.batchGroupingCode ?? 'No batch'),
          minOverride: null,
          maxOverride: null,
          minCycle: null,
          maxCycle: null,
          naturalCycle: null,
          cycleSetBy: '',
        },
      })
    })
    return out.sort(
      (a, b) =>
        String(a.header.workCentreCode).localeCompare(
          String(b.header.workCentreCode)
        ) || a.header.label.localeCompare(b.header.label)
    )
  }, [viewed])

  const filters = QUICK_FILTERS.total
  const quickFilter = filters.find((f) => f.value === quick)
  const visible = useMemo(() => {
    const text = search.trim().toLowerCase()
    const quickOk = (r: TotalRow) =>
      !quickFilter?.field || r[quickFilter.field] === quickFilter.equals
    const textOk = (r: TotalRow) =>
      !text ||
      [r.label, r.skuGroupName, r.skuGroupCode].some((v) =>
        String(v ?? '')
          .toLowerCase()
          .includes(text)
      )
    return groups.flatMap((g) => {
      const headerText = textOk(g.header)
      const kids = g.children.filter(
        (c) => quickOk(c) && (headerText || textOk(c))
      )
      const showHeader = kids.length > 0 || (headerText && quickOk(g.header))
      return showHeader ? [{ ...g, children: kids }] : []
    })
  }, [groups, quickFilter, search])

  const counts = useMemo(() => {
    const skus = viewed.filter((r) => !isBatch(r))
    const count = (field: string, value: string) =>
      skus.filter((r) => r[field] === value).length
    return {
      batches: viewed.filter(isBatch).length,
      skus: skus.length,
      override: count('cycleSetBy', 'Override'),
      batch: count('cycleSetBy', 'Batch'),
      foq: count('replenishmentTypeCode', 'FOQ'),
      none: count('cycleSetBy', 'No demand'),
    }
  }, [viewed])

  const quickOptions = filters.map((f) => ({
    value: f.value,
    label: f.field
      ? `${f.label} (${viewed.filter((r) => r[f.field as string] === f.equals).length})`
      : f.label,
  }))

  const setOverride = (values: OverrideEdit) => {
    if (menu) editor.setOverride(menu.row.id, values)
    setMenu(null)
  }

  const onStep = (row: TotalRow, cycle: number, anchor: HTMLElement) => {
    if (row.id.startsWith('orphan|')) return
    setMenu({ row, cycle, anchor })
  }

  const toggle = (id: string) =>
    setCollapsed((before) => {
      const next = new Set(before)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const headerLabelSx = {
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    color: 'var(--ink-500)',
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        gap: 3,
      }}
    >
      <NavigationModal
        open={blocker.state === 'blocked'}
        handleCancel={() => blocker.state === 'blocked' && blocker.reset()}
        handleProceed={() => blocker.state === 'blocked' && blocker.proceed()}
      />
      <KpiStrip>
        <KpiTile label="Batch groups" value={counts.batches} />
        <KpiTile label="SKU groups" value={counts.skus} />
        <KpiTile label="Overridden" value={counts.override} />
        <KpiTile label="Held by batch" value={counts.batch} />
        <KpiTile label="FOQ" value={counts.foq} />
        <KpiTile label="No demand" value={counts.none} />
      </KpiStrip>

      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
      >
        <SegmentedControl
          label="Show rows"
          value={quick}
          options={quickOptions}
          onChange={setQuick}
        />
        <TextField
          size="small"
          placeholder="Filter batch or SKU group"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 240 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 16 }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="text"
          startIcon={<UnfoldMoreOutlined />}
          onClick={() => setCollapsed(new Set())}
        >
          Expand all
        </Button>
        <Button
          size="small"
          variant="text"
          startIcon={<UnfoldLessOutlined />}
          onClick={() => setCollapsed(new Set(groups.map((g) => g.header.id)))}
        >
          Collapse all
        </Button>
        {editor.pendingCount > 0 && (
          <>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ fontSize: 12, color: 'var(--ink-500)' }}>
              {editor.pendingCount} row{editor.pendingCount === 1 ? '' : 's'}{' '}
              edited
            </Box>
            <Button
              size="small"
              variant="text"
              startIcon={<UndoOutlined />}
              disabled={editor.isSaving}
              onClick={editor.discard}
            >
              Discard
            </Button>
            <Tooltip title={editor.saveBlockedReason ?? ''}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SaveOutlined />}
                  loading={editor.isSaving}
                  disabled={
                    editor.isPreviewing || Boolean(editor.saveBlockedReason)
                  }
                  onClick={editor.save}
                >
                  Save
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </Box>

      <LinePreviewNote
        summary={editor.summary}
        isPreviewing={editor.isPreviewing}
        problems={editor.problems}
      />

      <Box
        sx={{
          flex: 1,
          minHeight: 320,
          overflow: 'auto',
          border: '1px solid var(--line-200)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--surface-0)',
        }}
      >
        {/* Column headers */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 32,
            minWidth: MIN_WIDTH,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            background: 'var(--panel-head)',
            borderBottom: '1px solid var(--line-300)',
            ...headerLabelSx,
          }}
        >
          <Box
            sx={{
              width: RAIL,
              minWidth: RAIL,
              pl: '8px',
              position: 'sticky',
              left: 0,
              background: 'var(--panel-head)',
              alignSelf: 'stretch',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Batch / SKU group
          </Box>
          <HeaderCell width={NUM_W}>Sales</HeaderCell>
          <HeaderCell width={NUM_W}>Min order</HeaderCell>
          <HeaderCell width={NUM_W}>Natural</HeaderCell>
          <Box
            sx={{
              flex: 1,
              minWidth: TRACK_MIN,
              display: 'flex',
              alignSelf: 'stretch',
              borderLeft: '1px solid var(--line-200)',
              borderRight: '1px solid var(--line-200)',
            }}
          >
            {CYCLES.map((cycle) => (
              <Box
                key={cycle}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {cycle} wk
              </Box>
            ))}
          </Box>
          <HeaderCell width={OVR_W} align="center">
            Min ovr
          </HeaderCell>
          <HeaderCell width={OVR_W} align="center">
            Max ovr
          </HeaderCell>
          <HeaderCell width={CYCLE_W} align="center">
            Cycle
          </HeaderCell>
          <HeaderCell width={TYPE_W} align="center">
            Type
          </HeaderCell>
        </Box>

        {visible.length === 0 && (
          <Box sx={{ p: 6, fontSize: 13, color: 'var(--ink-500)' }}>
            No batch or SKU groups match.
          </Box>
        )}
        {visible.map((g) => {
          const isCollapsed = collapsed.has(g.header.id)
          return (
            <Box key={g.header.id}>
              <CycleRow
                row={g.header}
                header
                childCount={g.children.length}
                collapsed={isCollapsed}
                onToggle={() => toggle(g.header.id)}
                edited={editor.isEdited(g.header.id)}
                previewed={editor.changedFields(g.header.id)}
                problems={editor.problemsFor(g.header.id)}
                onStep={onStep}
              />
              {!isCollapsed &&
                g.children.map((child) => (
                  <CycleRow
                    key={child.id}
                    row={child}
                    header={false}
                    edited={editor.isEdited(child.id)}
                    previewed={editor.changedFields(child.id)}
                    problems={editor.problemsFor(child.id)}
                    onStep={onStep}
                  />
                ))}
            </Box>
          )
        })}
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 4,
          flexWrap: 'wrap',
          fontSize: 11,
          color: 'var(--ink-500)',
          alignItems: 'center',
        }}
      >
        <Legend
          swatch={{
            background: 'var(--brand-blue-50)',
            border: '1px solid var(--brand-blue)',
            borderRadius: '6px',
          }}
          label="Min to max cycle"
        />
        <Legend
          swatch={{
            background: 'var(--amber-tint)',
            border: '1px solid var(--amber-fg)',
            borderRadius: '6px',
          }}
          label="With an override (dot: the overridden end)"
        />
        <Legend
          swatch={{ width: 2, background: 'var(--ink-700)' }}
          label="Natural cycle"
        />
        <Legend
          swatch={{
            background:
              'repeating-linear-gradient(135deg, var(--line-300) 0 2px, transparent 2px 6px)',
          }}
          label="Faster than its batch"
        />
        <Legend
          swatch={{
            background: 'var(--surface-warn)',
            border: '1px dashed var(--amber-fg)',
          }}
          label="Edited, not saved"
        />
      </Box>

      <Menu
        open={Boolean(menu)}
        anchorEl={menu?.anchor}
        onClose={() => setMenu(null)}
      >
        {menu && [
          <ListSubheader key="title" sx={{ lineHeight: '28px', fontSize: 11 }}>
            {isBatch(menu.row) ? 'Batch' : 'SKU group'} {menu.row.label} at{' '}
            {menu.cycle} wk
          </ListSubheader>,
          <MenuItem
            key="min"
            dense
            disabled={menu.row.minOverride === menu.cycle}
            onClick={() => setOverride({ minOverride: menu.cycle })}
          >
            Min override: no more often than every {menu.cycle} wk
          </MenuItem>,
          <MenuItem
            key="max"
            dense
            disabled={menu.row.maxOverride === menu.cycle}
            onClick={() => setOverride({ maxOverride: menu.cycle })}
          >
            Max override: at least every {menu.cycle} wk
          </MenuItem>,
          <MenuItem
            key="fix"
            dense
            onClick={() =>
              setOverride({ minOverride: menu.cycle, maxOverride: menu.cycle })
            }
          >
            Fix at {menu.cycle} wk (min and max)
          </MenuItem>,
          <Divider key="divider" />,
          <MenuItem
            key="clear-min"
            dense
            disabled={menu.row.minOverride == null}
            onClick={() => setOverride({ minOverride: null })}
          >
            Clear the min override
          </MenuItem>,
          <MenuItem
            key="clear-max"
            dense
            disabled={menu.row.maxOverride == null}
            onClick={() => setOverride({ maxOverride: null })}
          >
            Clear the max override
          </MenuItem>,
        ]}
      </Menu>
    </Box>
  )
}

const Legend = ({
  swatch,
  label,
}: {
  swatch: Record<string, string | number>
  label: string
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    <Box sx={{ width: 18, height: 10, ...swatch }} />
    {label}
  </Box>
)
